import { mapActions, mapGetters, mapState } from 'vuex';
import _ from 'lodash-extended';
import tmBus from '../../api/tmBus';
import gridMixin from '../grid.mixin';
import fg from '#/libs/FrameGrid';
import {
  assignDurations,
  CursorManager,
  getAudioSnapTimesForVideoResizeRight,
  getResizeAvailability,
  mapAvailability2Cursor,
} from '../../api/tapi';
import { getGifSnapTimes } from '../../api/asset';

export default {
  mixins: [gridMixin],

  computed: {
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      secondhand: (state) => state.secondhand,
      pxAxisSidePadding: (state) => state.cssVariables.pxAxisSidePadding,
    }),
    ...mapGetters('timemachine', ['getBgBeats']),

    bgBeatsTimes() {
      return this.getBgBeats.map((i) => i.time);
    },

    sceneTimesFromLeft() {
      const { data } = this.track;
      // Get scene data for this asset
      const assetUUID = data.metadata.assetUUID;
      const allScenes =
        (app.rootStore.state.story && app.rootStore.state.story.scenes) || {};
      const assetScenes = allScenes[assetUUID] || [];
      return assetScenes.map((scene) =>
        fg.trunc(
          scene.startTime - data.startTime + data.displayStartTime + fg.SPF
        )
      );
    },

    sceneTimesFromRight() {
      const { data } = this.track;
      // Get scene data for this asset
      const assetUUID = data.metadata.assetUUID;
      const allScenes =
        (app.rootStore.state.story && app.rootStore.state.story.scenes) || {};
      const assetScenes = allScenes[assetUUID] || [];
      return assetScenes.map((scene) =>
        fg.trunc(
          scene.startTime - data.startTime + data.displayStartTime - fg.SPF * 6
        )
      );
    },

    moveObj() {
      return {
        enabled: !this.MODE_SCENE_EDIT,
        distance2trigger: this.CONST.DND_SENSITIVITY.RESIZE,
        onMoveStart: this.$_onMoveStart,
        onMove: this.$_onMove,
        onMoveEnd: this.$_onMoveEnd,
      };
    },
  },

  methods: {
    ...mapActions('timemachine', [
      'SET_MODE',
      'SET_DND_ESCORT',
      'SELECT_VISUAL',
      'SHIFT_VISUALS',
      'SET_SECONDHAND',
    ]),

    $_getOriginalCutTimes() {
      return new Promise((resolve) => {
        tmBus.$emit('callbackGetFragmentCutTimes', {
          track: this.track,
          fnCallback: resolve,
        });
      });
    },

    async $_onMoveStart(state) {
      this.SET_MODE({ name: 'ASSET_RESIZE', value: true });
      this.SET_DND_ESCORT({ name: 'useScrollSensor', value: true });
      this.SELECT_VISUAL({ track: this.track });
      this.track.isResizing = true;
      this.$data.resizing = true;
      const { data } = this.track;

      state.mousePoint = this.$_mouse2point(state.eDown);

      state.initial = assignDurations({}, data);
      state.aTimes = [];
      // const originalCutTimes = await this.$_getOriginalCutTimes();
      // if (originalCutTimes) {
      //   state.aTimes.push(
      //     fg.trunc(
      //       data.displayEndTime - (data.endTime - originalCutTimes.startTime)
      //     ),
      //     fg.trunc(
      //       data.displayEndTime - (data.endTime - originalCutTimes.endTime)
      //     )
      //   );
      // }
      if (this.$props.fromLeft) {
        state.shiftX = this.rectangle.p1.x - state.mousePoint.x;

        state.frameSeekTime = null;
        if (
          _.between(data.displayStartTime, this.secondhand, data.displayEndTime)
        ) {
          state.frameSeekTime = fg.trunc(data.displayEndTime - this.secondhand);
        }
        state.aTimes.push(...this.sceneTimesFromLeft);
        console.log('left', this.sceneTimesFromLeft);
      } else {
        state.shiftX = this.rectangle.p2.x - state.mousePoint.x;
        state.aTimes.push(
          this.secondhand,
          ...getGifSnapTimes(data),
          ...getAudioSnapTimesForVideoResizeRight(this.trackLayer.audio),
          ...this.bgBeatsTimes,
          ...this.sceneTimesFromRight
        );
        console.log('right', this.sceneTimesFromRight);
      }

      state.proximityTester = this.$_createProximityTester(
        state.aTimes,
        this.CONST.AUTOSNAP_PROXIMITY.RESIZE
      );
      state.videoSpeed = data.agd.videoSpeed || 1;
      state.maxTimeShifted = state.initial.displayEndTime;
      state.timeWindow = this.track.pipa.getSceneResizeTimeWindow({
        relativeToScene: false,
      });

      if (this.$props.fromRight) {
        state.unWatch = this.$watch('bgBeatsTimes', () => {
          if (state.aTimes.length) {
            state.shouldRecalculateProximity = true;
          }
        });
      }

      tmBus.$emit('callbackAssetResizeStart', data);
      state.canDoMove = true;
    },

    $_onMove(state, autoStep) {
      if (!state.canDoMove) {
        return;
      }
      const { data } = this.track;
      this.$_mouse2point(state.eMove, state.mousePoint, state.shiftX);

      if (this.$props.fromLeft) {
        this.$_moveFromLeft(state, autoStep);
      } else {
        this.$_moveFromRight(state);
      }

      if (data.displayEndTime >= state.maxTimeShifted) {
        this.SHIFT_VISUALS({ afterTrack: this.track });
        state.maxTimeShifted = data.displayEndTime;
      }

      const resizeWindow = this.track.pipa.getSceneResizeTimeWindow({
        relativeToScene: false,
      });
      const availability = getResizeAvailability(
        resizeWindow,
        this.track.pipa.scene
      );
      const cursorMap = mapAvailability2Cursor(availability);
      CursorManager.set(
        this.$props.fromLeft ? cursorMap.left : cursorMap.right
      );

      tmBus.$emit('callbackAssetResizeUpdate', {
        asset: this.track.data,
        fromLeft: this.$props.fromLeft,
      });
    },

    $_setLeftDurationChange(state, autoStep) {
      state.autoPilot = state.autoPilot || {
        on: false,
        accumulatedDistance: 0,
        direction: 1,
        step: this.rowDuration * 0.001,
        animationFrame: null,
        off() {
          cancelAnimationFrame(this.animationFrame);
          this.animationFrame = null;
        },
        animate: () => {
          state.autoPilot.animationFrame = requestAnimationFrame(() => {
            if (state.canceled) {
              state.autoPilot.off();
              return;
            }

            state.autoPilot.accumulatedDistance +=
              state.autoPilot.direction * state.autoPilot.step;

            this.$_onMove(state, true);
            state.autoPilot.animate();
          });
        },
      };

      let curTime = fg.trunc(state.mousePoint.time);
      const snapTime = state.proximityTester.find(curTime);
      if (null !== snapTime) {
        curTime = snapTime;
      }
      const delta = state.initial.displayStartTime - curTime;
      state.durationChange = fg.trunc(
        delta + state.autoPilot.accumulatedDistance
      );

      state.autoPilot.on =
        state.initial.displayStartTime === 0 &&
        state.mousePoint.x < this.pxAxisSidePadding;

      const shouldStart = state.autoPilot.on && !state.autoPilot.animationFrame;
      const shouldStop = !state.autoPilot.on && state.autoPilot.animationFrame;

      if (shouldStart) {
        state.autoPilot.animate();
      } else if (shouldStop) {
        state.autoPilot.off();
      }
    },

    $_moveFromLeft(state, autoStep) {
      this.$_setLeftDurationChange(state, autoStep);
      if (state.autoPilot.on && !autoStep) {
        return;
      }

      let newDuration = fg.trunc(
        state.initial.displayDuration + state.durationChange
      );
      const { data } = this.track;

      const minDuration = state.timeWindow.left.minDuration;
      let maxDuration = state.timeWindow.left.maxDuration;
      if (data.limit.fragment) {
        maxDuration =
          (data.endTime - data.limit.fragment.min) / state.videoSpeed;
      }

      newDuration = _.constrain(minDuration, newDuration, maxDuration);

      // shrink or extend from left
      if (
        state.initial.displayDuration + state.autoPilot.accumulatedDistance >=
        newDuration
      ) {
        data.displayStartTime = Math.max(
          0,
          fg.trunc(
            state.initial.displayEndTime -
              newDuration +
              state.autoPilot.accumulatedDistance
          )
        );
        data.displayEndTime = fg.trunc(
          state.initial.displayEndTime + state.autoPilot.accumulatedDistance
        );
      } else {
        data.displayStartTime = state.initial.displayStartTime;
        data.displayEndTime = fg.trunc(
          state.initial.displayStartTime + newDuration
        );
      }

      data.displayDuration = fg.trunc(
        data.displayEndTime - data.displayStartTime
      );

      if (data.limit.fragment) {
        data.startTime = fg.trunc(
          data.endTime - data.displayDuration * state.videoSpeed
        );
      }

      this.track.resizeEscort.START_TIME_SHIFT = fg.trunc(
        state.initial.displayDuration - data.displayDuration
      );
    },

    $_moveFromRight(state) {
      const { data } = this.track;
      const delta = state.mousePoint.time - state.initial.displayEndTime;
      state.durationChange = delta;

      const minEndTime = state.timeWindow.right.min;
      let maxEndTime = state.timeWindow.right.max;

      if (data.limit.fragment) {
        // limit enlarging of fragment endTime
        maxEndTime =
          data.displayStartTime +
          (data.limit.fragment.max - data.startTime) / state.videoSpeed;
      }

      let endTime = fg.trunc(
        data.displayStartTime +
          (state.initial.displayDuration + state.durationChange)
      );

      if (state.shouldRecalculateProximity) {
        state.shouldRecalculateProximity = false;
        state.aTimes = [...state.aTimes, ...this.bgBeatsTimes];
        state.proximityTester = this.$_createProximityTester(
          state.aTimes,
          this.CONST.AUTOSNAP_PROXIMITY.RESIZE
        );
      }

      const snapTime = state.proximityTester.find(endTime);
      if (null !== snapTime) {
        endTime = snapTime;
      }

      endTime = _.constrain(
        fg.trunc(minEndTime),
        endTime,
        fg.trunc(maxEndTime)
      );

      data.displayEndTime = endTime;
      data.displayDuration = fg.trunc(
        data.displayEndTime - data.displayStartTime
      );

      if (data.limit.fragment) {
        data.endTime = fg.trunc(
          data.startTime + data.displayDuration * state.videoSpeed
        );
      }

      this.track.resizeEscort.END_TIME_SHIFT = fg.trunc(
        data.displayEndTime - state.initial.displayEndTime
      );
    },

    $_onMoveEnd(state) {
      state.unWatch && state.unWatch();
      CursorManager.restore();

      let seekToTime = this.secondhand;
      const { data } = this.track;

      if (state.canceled) {
        assignDurations(data, state.initial);
      }

      if (this.$props.fromLeft) {
        state.autoPilot.off();

        if (data.displayDuration < state.initial.displayDuration) {
          this.track.pipa.cutFromLeft(
            fg.trunc(state.initial.displayDuration - data.displayDuration)
          );
        } else if (data.displayDuration > state.initial.displayDuration) {
          this.track.pipa.shiftOverlaysBy(
            fg.trunc(data.displayDuration - state.initial.displayDuration)
          );
        }

        this.SHIFT_VISUALS({ beforeTrack: this.track });

        if (null !== state.frameSeekTime) {
          if (state.frameSeekTime > data.displayDuration) {
            seekToTime = data.displayStartTime + 0.001;
          } else {
            seekToTime = fg.trunc(data.displayEndTime - state.frameSeekTime);
          }
        }
      } else {
        if (data.displayDuration < state.initial.displayDuration) {
          this.track.pipa.cutFromRight(data.displayDuration);
        }

        this.SHIFT_VISUALS({ afterTrack: this.track });
      }

      this.SET_SECONDHAND({ time: seekToTime });
      this.SET_DND_ESCORT({ name: 'useScrollSensor', value: false });
      this.SET_MODE({ name: 'ASSET_RESIZE', value: false });
      this.track.isResizing = false;
      this.track.resizeEscort.START_TIME_SHIFT = 0;
      this.track.resizeEscort.END_TIME_SHIFT = 0;
      this.$data.resizing = false;
      tmBus.$emit('callbackAssetResizeStop', data);
      tmBus.$emit('callbackChange', { e: state.eUp, seekToTime });
    },
  },
};
